import { useCallback, useRef, useState } from "react";
import { sendChat, deleteSession } from "./fushiClient";

const GREETING_TEXT = "Hi, I'm Fushi, How can I help you today :)";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// A fresh greeting message (new id so React re-keys it on "New chat").
const makeGreeting = () => ({ id: newId(), role: "assistant", content: GREETING_TEXT });

/**
 * Owns the displayed message list and the live session_id.
 * The frontend holds the messages it shows; the server owns the reasoning
 * history behind the session_id and only ever returns the latest reply.
 */
export default function useFushiChat() {
  const [messages, setMessages] = useState(() => [makeGreeting()]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionIdRef = useRef(null); // persists across renders, never shown in UI

  const send = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || isLoading) return;

      setMessages((m) => [...m, { id: newId(), role: "user", content: trimmed }]);
      setIsLoading(true);

      try {
        const data = await sendChat({ message: trimmed, sessionId: sessionIdRef.current });
        sessionIdRef.current = data.session_id; // capture on 1st turn, reuse after
        setMessages((m) => [
          ...m,
          { id: newId(), role: "assistant", content: data.reply },
        ]);
      } catch (e) {
        setMessages((m) => [
          ...m,
          {
            id: newId(),
            role: "assistant",
            content: "⚠️ Sorry, I couldn't reach Fushi right now. Please try again in a moment.",
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading]
  );

  const newChat = useCallback(() => {
    const previous = sessionIdRef.current;
    sessionIdRef.current = null;
    setMessages([makeGreeting()]);
    deleteSession(previous); // fire-and-forget cleanup
  }, []);

  return { messages, isLoading, send, newChat };
}
